import React from "react";
import {
  SQUAD_DATE_PREFIX,
  groupSquadsByDate,
  squadDateLabel,
  squadSelectOptionLabel,
} from "./scoringFilterOptions";

export const squadSelectClassName = (compact) =>
  compact
    ? "h-11 min-h-11 min-w-[11rem] max-w-full sm:max-w-[18rem] px-3 text-sm border border-brand-charcoal/20 rounded-lg bg-white text-brand-charcoal focus:outline-none focus:ring-2 focus:ring-brand-gold-ink"
    : "w-full px-3 py-2 border border-brand-charcoal/20 rounded-lg bg-white text-brand-charcoal focus:outline-none focus:ring-2 focus:ring-brand-gold-ink";

const SquadSelect = ({
  squads = [],
  value = "",
  onChange,
  allLabel = null,
  allowAllTimes,
  placeholder = "Choose a squad",
  emptyLabel = "No squads",
  label,
  required = false,
  disabled = false,
  compact = false,
  error,
  id,
  className = "",
}) => {
  const includeAllTimes =
    allowAllTimes === undefined ? Boolean(allLabel) : allowAllTimes;
  const groups = groupSquadsByDate(squads);
  const useOptgroup = groups.length > 1 && groups.every((group) => group.date);
  const generatedId = React.useId();
  const selectId = id || (label ? `squad-select-${generatedId}` : undefined);

  const groupNodes = groups.map((group) => {
    const dateLabel = squadDateLabel(group.date);
    const allTimes =
      includeAllTimes && group.squads.length > 1 && group.date ? (
        <option
          key={`${SQUAD_DATE_PREFIX}${group.date}`}
          value={`${SQUAD_DATE_PREFIX}${group.date}`}
        >
          {useOptgroup ? "All times" : `${dateLabel} · all times`}
        </option>
      ) : null;
    const timeOptions = group.squads.map((squad) => (
      <option key={squad.id} value={squad.id}>
        {squadSelectOptionLabel(squad, squads, {
          groupedByDate: useOptgroup,
        })}
      </option>
    ));
    if (useOptgroup) {
      return (
        <optgroup key={group.date} label={dateLabel}>
          {allTimes}
          {timeOptions}
        </optgroup>
      );
    }
    return (
      <React.Fragment key={group.date || "squads"}>
        {allTimes}
        {timeOptions}
      </React.Fragment>
    );
  });

  const select = (
    <select
      id={selectId}
      aria-label={label ? undefined : "Squad"}
      value={value}
      disabled={disabled}
      required={required}
      onChange={(event) => onChange(event.target.value)}
      className={squadSelectClassName(compact)}
    >
      {allLabel ? <option value="all">{allLabel}</option> : null}
      {!allLabel && squads.length === 0 ? (
        <option value="">{emptyLabel}</option>
      ) : null}
      {!allLabel && squads.length > 0 ? (
        <option value="">{placeholder}</option>
      ) : null}
      {groupNodes}
    </select>
  );

  if (!label && !error) {
    return select;
  }

  return (
    <div className={className}>
      {label ? (
        <label
          htmlFor={selectId}
          className="block text-sm font-medium text-brand-charcoal mb-2"
        >
          {label}
          {required ? <span className="text-red-500"> *</span> : null}
        </label>
      ) : null}
      {select}
      {error ? <p className="mt-1 text-sm text-red-600">{error}</p> : null}
    </div>
  );
};

export default SquadSelect;
